import { produce } from "immer";
import { filter, firstValueFrom, Subject } from "rxjs";
import scrollIntoView from "scroll-into-view-if-needed";
import Cursor from "./cursor";
import { caretPositionFromPoint, clearDOMSelection, convertPositionFromDOM, convertPositionToDOM, doesEditorHasFocus, findEditorElement, hasRevisionRendered, isEditorActive, withInsertNodeTemporarily, writeSelectionToDOM, } from "./dom-utils";
import { globalInputDelegate } from "./input";
import { applyOperation, applyOperationToCursor } from "./operations_old";
import PerformChangeContext from "./perform-change-context";
import { areFormatsEqual, areSelectionsEqual, assert, DEFAULT_FORMAT, getElementByPath, getUid, newSelection, } from "./utils";
import { Text } from "./view/content";
export const DEFAULT_PRESET = {
    extensions: [],
};
export const normalizeConfig = (config) => {
    var _a, _b, _c;
    const extensions = [];
    const processed = new Set();
    const process = (ext) => {
        if (processed.has(ext.id))
            return;
        processed.add(ext.id);
        if (ext.deps != null) {
            ext.deps.forEach(process);
        }
        extensions.push(ext);
    };
    [
        ...((_b = (_a = config === null || config === void 0 ? void 0 : config.preset) === null || _a === void 0 ? void 0 : _a.extensions) !== null && _b !== void 0 ? _b : []),
        ...((_c = config === null || config === void 0 ? void 0 : config.extensions) !== null && _c !== void 0 ? _c : []),
    ].forEach(process);
    return {
        extensions,
    };
};
export const configure = (config = {}) => {
    const { extensions } = normalizeConfig(config);
    const elementComponents = new Map();
    let textComponent = Text;
    let inputHook = null;
    const extApi = {
        overrideTextComponent: (overrider) => {
            textComponent = overrider(textComponent);
        },
        setElementComponent: (tag, component) => {
            elementComponents.set(tag, component);
        },
        setInputHook: (hook) => {
            inputHook = hook;
        },
    };
    for (const ext of extensions) {
        ext.initialize(extApi);
    }
    class EditorModel {
        constructor(editorKey) {
            this.editorElement = null;
            this.readonly = false;
            this.content = { items: [], length: 0 };
            this.revision = 0;
            this.format = DEFAULT_FORMAT;
            this.markers = [];
            this.readonlyChange$ = new Subject();
            this.isChanging = false;
            this.change$ = new Subject();
            this.contentChange$ = new Subject();
            this.beforeInput$ = new Subject();
            this.afterInput$ = new Subject();
            this.selectionChange$ = new Subject();
            this.lockSelectionUntil = null;
            this.formatChange$ = new Subject();
            this.markersChange$ = new Subject();
            this.activeChange$ = new Subject();
            this.trackedCursors = new Set();
            this.anchorCursor = null;
            this.focusCursor = null;
            this.selection = null;
            this.contentToRevsion = new WeakMap();
            this.selectionToRevision = new WeakMap();
            this.contentRendered$ = new Subject();
            this.isComposing = false;
            this.composingChange$ = new Subject();
            this.recoverHandler = null;
            this.editorKey = `${editorKey !== null && editorKey !== void 0 ? editorKey : getUid(this)}`;
            this.contentToRevsion.set(this.content, this.revision);
        }
        get __brand() {
            return "e0386e77-bf07-4483-86ee-7fa55efcb46c";
        }
        get onComposingChange() {
            return this.composingChange$;
        }
        setComposing(composing) {
            if (this.isComposing !== composing) {
                this.isComposing = composing;
                this.composingChange$.next();
            }
        }
        get onReadonlyChange() {
            return this.readonlyChange$;
        }
        get onContentChange() {
            return this.contentChange$;
        }
        get onChange() {
            return this.change$;
        }
        get onBeforeInput() {
            return this.beforeInput$;
        }
        get onAfterInput() {
            return this.afterInput$;
        }
        get onSelectionChange() {
            return this.selectionChange$;
        }
        get onFormatChange() {
            return this.formatChange$;
        }
        get onMarkersChange() {
            return this.markersChange$;
        }
        get onContentRendered() {
            return this.contentRendered$;
        }
        get onActiveChange() {
            return this.activeChange$;
        }
        get active() {
            return isEditorActive(this.editorElement);
        }
        get hasFocus() {
            return doesEditorHasFocus(this.editorKey);
        }
        setReadonly(readonly) {
            if (this.readonly !== readonly) {
                this.readonly = readonly;
                this.readonlyChange$.next();
            }
        }
        emitActiveChange() {
            this.activeChange$.next();
            if (this.active && this.selection === null) {
                this.setSelection(newSelection(0, 0));
            }
        }
        async requestFocus(opts) {
            const selection = this.selection;
            await this.waitForContentRendered();
            // NOTE: Don't remove the comment below
            // if (this.active) return;
            const editorElement = findEditorElement(this.editorKey);
            if (document.activeElement !== editorElement) {
                this.setLockSelectionUntil(Date.now() + 25);
                editorElement.focus({
                    preventScroll: opts === null || opts === void 0 ? void 0 : opts.preventScroll,
                });
                await new Promise((resolve) => setTimeout(resolve, 25));
            }
            writeSelectionToDOM([this.editorKey, selection !== null && selection !== void 0 ? selection : newSelection(0, 0)]);
        }
        async waitForContentRendered() {
            if (this.isContentRendered)
                return;
            await firstValueFrom(this.contentRendered$.pipe(filter(() => this.isContentRendered)));
        }
        emitContentRendered() {
            this.contentRendered$.next();
        }
        createCursor(offset, prefer) {
            return new Cursor(this, offset, prefer);
        }
        mark(offset, endOffset, data) {
            assert(endOffset >= offset);
            assert(offset >= 0);
            assert(endOffset <= this.content.length);
            const startCursor = new Cursor(this, offset, "right");
            const endCursor = new Cursor(this, endOffset, "left");
            const marker = {
                get offset() {
                    return startCursor.offset;
                },
                get endOffset() {
                    return endCursor.offset;
                },
                data,
                remove: () => {
                    assert(this.markers.includes(marker));
                    startCursor.release();
                    endCursor.release();
                    this.removeMarker(marker);
                },
                updateData: (data) => {
                    assert(this.markers.includes(marker));
                    marker.data = data;
                    this.markersChange$.next();
                },
                updateRange: (offset, endOffset) => {
                    assert(this.markers.includes(marker));
                    this.removeMarker(marker);
                    startCursor.offset = offset;
                    endCursor.offset = endOffset;
                    this.addMarker(marker);
                },
            };
            this.addMarker(marker);
            return marker;
        }
        addMarker(marker) {
            this.markers.push(marker);
            this.markers.sort((marker1, marker2) => {
                if (marker1.offset === marker2.offset) {
                    if (marker1.endOffset === marker2.endOffset)
                        return 0;
                    if (marker1.endOffset < marker2.endOffset)
                        return -1;
                    return 1;
                }
                if (marker1.offset < marker2.offset)
                    return -1;
                return 1;
            });
            this.markersChange$.next();
        }
        removeMarker(marker) {
            const index = this.markers.indexOf(marker);
            if (index >= 0) {
                this.markers.splice(index, 1);
                this.markersChange$.next();
            }
        }
        performChange(callback, key) {
            assert(!this.isChanging);
            this.isChanging = true;
            const prevContent = this.content;
            const prevSelection = this.selection;
            const prevFormat = this.format;
            const context = new PerformChangeContext(this);
            callback(context);
            if (this.content !== prevContent) {
                this.revision += 1;
                this.contentToRevsion.set(this.content, this.revision);
            }
            if (this.selection !== null) {
                this.selectionToRevision.set(this.selection, this.revision);
            }
            this.isChanging = false;
            Promise.resolve().then(() => {
                if (this.content !== prevContent) {
                    this.contentChange$.next();
                }
                if (!areSelectionsEqual(this.selection, prevSelection)) {
                    this.selectionChange$.next({ fromInput: false });
                }
                if (this.content !== prevContent ||
                    !areSelectionsEqual(this.selection, prevSelection)) {
                    this.change$.next({
                        key,
                        previousContent: prevContent,
                        previousSelection: prevSelection,
                    });
                }
                if (!areFormatsEqual(this.format, prevFormat)) {
                    this.formatChange$.next();
                }
            });
        }
        get isContentRendered() {
            return hasRevisionRendered(this.editorElement, this.revision);
        }
        getBoundingClientRectOfRange(startOffset, endOffset) {
            if (!this.isContentRendered)
                return null;
            const startPos = convertPositionToDOM([this.editorKey, startOffset]);
            const endPos = convertPositionToDOM([this.editorKey, endOffset]);
            const range = new Range();
            range.setStart(startPos[0], startPos[1]);
            range.setEnd(endPos[0], endPos[1]);
            return range.getBoundingClientRect();
        }
        hitTest(x, y) {
            if (!this.isContentRendered)
                return null;
            const domPos = caretPositionFromPoint(x, y);
            if (domPos === null)
                return null;
            const result = convertPositionFromDOM(domPos);
            if (result === null)
                return null;
            const [editorKey, offset] = result;
            if (editorKey !== this.editorKey)
                return null;
            return offset;
        }
        async scrollCaretIntoViewIfNeeded() {
            await this.waitForContentRendered();
            if (this.selection === null)
                return;
            if (!this.selection.isCollapsed)
                return;
            // TODO: Avoid the costly reflow
            const [container, offset] = convertPositionToDOM([
                this.editorKey,
                this.selection.focusOffset,
            ]);
            const span = document.createElement("span");
            span.textContent = "\ufeff";
            withInsertNodeTemporarily(container, offset, span, () => {
                scrollIntoView(span, {
                    scrollMode: "if-needed",
                    block: "nearest",
                    behavior: (actions) => {
                        for (const action of actions) {
                            action.el.scrollLeft = action.left;
                            action.el.scrollTop = action.top;
                        }
                    },
                });
            });
            globalInputDelegate.renderSelection(this.editorKey, this.selection);
        }
        setFormat(format) {
            assert(this.isChanging);
            this.format = format;
        }
        setSelection(selection) {
            if (selection !== null &&
                (selection.anchorOffset < 0 ||
                    selection.anchorOffset > this.content.length ||
                    selection.focusOffset < 0 ||
                    selection.focusOffset > this.content.length)) {
                console.warn("Set selection out of content range");
                const anchorOffset = Math.min(Math.max(0, selection.anchorOffset), this.content.length);
                const focusOffset = Math.min(Math.max(selection.focusOffset, 0), this.content.length);
                selection = newSelection(anchorOffset, focusOffset);
            }
            this.selection = selection;
        }
        setContent(content, selection = null) {
            assert(this.isChanging);
            this.content = content;
            this.setSelection(selection);
        }
        updateElementProps(path, newProps) {
            this.content = produce(this.content, (draft) => {
                const element = getElementByPath(draft, path);
                Object.assign(element.props, newProps);
            });
        }
        applyOperation(op) {
            var _a, _b, _c, _d, _e;
            assert(this.contentChange$ !== null);
            const prevContent = this.content;
            const nextContent = produce(this.content, (draft) => {
                applyOperation(draft, op);
            });
            const hasSelection = this.selection !== null;
            const anchorCursor = new Cursor(this, (_b = (_a = this.selection) === null || _a === void 0 ? void 0 : _a.anchorOffset) !== null && _b !== void 0 ? _b : 0, ((_c = this.selection) === null || _c === void 0 ? void 0 : _c.isCollapsed) ? "right" : "left");
            const focusCursor = new Cursor(this, (_e = (_d = this.selection) === null || _d === void 0 ? void 0 : _d.focusOffset) !== null && _e !== void 0 ? _e : 0, "right");
            for (const cursor of this.trackedCursors) {
                const offset = applyOperationToCursor(prevContent, cursor.offset, op, cursor.prefer);
                cursor.offset = offset;
            }
            const nextSelection = hasSelection
                ? newSelection(anchorCursor.offset, focusCursor.offset)
                : null;
            anchorCursor.release();
            focusCursor.release();
            this.setContent(nextContent, nextSelection);
        }
        getTextComponent() {
            return textComponent;
        }
        getElementComponent(tag) {
            return elementComponents.get(tag);
        }
        getContentRevision(content) {
            return this.contentToRevsion.get(content);
        }
        getSelectionRevision(selection) {
            return this.selectionToRevision.get(selection);
        }
        inputInsert(content, type) {
            var _a, _b, _c, _d;
            if (this.readonly)
                return;
            if (this.isChanging)
                return;
            if (this.selection === null) {
                this.setSelection(newSelection(0, 0));
            }
            if (inputHook !== null) {
                const returnValue = inputHook(this, content, type);
                if (returnValue === false) {
                    return;
                }
            }
            const beforeInputRevision = this.revision;
            if (type === "input") {
                assert(typeof content === "string");
                this.beforeInput$.next({
                    content,
                    type,
                    offset: (_b = (_a = this.selection) === null || _a === void 0 ? void 0 : _a.offset) !== null && _b !== void 0 ? _b : 0,
                });
            }
            else {
                this.beforeInput$.next({
                    content,
                    type,
                    offset: (_d = (_c = this.selection) === null || _c === void 0 ? void 0 : _c.offset) !== null && _d !== void 0 ? _d : 0,
                });
            }
            this.performChange((ctx) => {
                ctx.delete().insert(content);
            });
            void this.scrollCaretIntoViewIfNeeded();
            this.afterInput$.next({ beforeInputRevision });
        }
        inputDelete(offset, endOffset) {
            if (this.readonly)
                return;
            if (this.isChanging)
                return;
            assert(this.selection !== null);
            const beforeInputRevision = this.revision;
            this.beforeInput$.next({
                type: "delete",
                offset,
                length: endOffset - offset,
            });
            this.performChange((ctx) => {
                ctx.shadow().select(offset, endOffset).delete().release();
            });
            this.afterInput$.next({ beforeInputRevision });
            void this.scrollCaretIntoViewIfNeeded();
        }
        setLockSelectionUntil(time) {
            this.lockSelectionUntil = time;
        }
        inputSelection(selection) {
            if (this.isChanging)
                return;
            if (!this.isContentRendered)
                return;
            if (this.lockSelectionUntil != null &&
                Date.now() < this.lockSelectionUntil) {
                if (this.selection != null) {
                    writeSelectionToDOM([this.editorKey, this.selection]);
                }
                else {
                    clearDOMSelection();
                }
                return;
            }
            const prevSelection = this.selection;
            this.setSelection(selection);
            if (selection !== null) {
                this.selectionToRevision.set(selection, this.revision);
            }
            if (!areSelectionsEqual(this.selection, prevSelection)) {
                this.selectionChange$.next({ fromInput: true });
            }
        }
        setRecoverHandler(handler) {
            this.recoverHandler = handler;
        }
        explicitRecover() {
            var _a;
            (_a = this.recoverHandler) === null || _a === void 0 ? void 0 : _a.call(this);
        }
        setEditorElement(element) {
            this.editorElement = element;
        }
    }
    return EditorModel;
};
